// A NIF for a child. user-backend writes these fields on the case (contract:
// coepi-bold docs/minors/contract.md); the portal only reads them, apart from
// guardian_match_override, which the lawyer sets. Every rule here applies only
// when applicant_type is "minor": an adult case has none of these fields and
// renders exactly as before.

export type MinorDocumentKind = 'passport' | 'id_card' | 'birth_certificate' | string;

export interface Guardian {
  index: number;
  relation?: string;
  user_id?: string | null;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  birthdate?: string | null;
  nationality?: string | null;
  document_kind?: MinorDocumentKind | null;
  document_number?: string | null;
  expiry_date?: string | null;
  identity_document_id?: string | null;
  signature_status?: 'pending' | 'signed' | string | null;
  signed_at?: any;
}

export interface SoleGuardian {
  reason?: 'sole_responsibility' | 'deceased' | 'court_order' | string | null;
  evidence_document_id?: string | null;
}

export interface GuardianMatchEntry {
  matched?: boolean | null;
  name?: string | null;
  note?: string | null;
}

export interface GuardianMatchOverride {
  value: string;
  reason: string;
  by?: string | null;
  at?: any;
}

export type GuardianMatchStatus = 'verified' | 'review' | 'unavailable';

export interface MinorCaseFields {
  applicant_type?: 'adult' | 'minor' | string | null;
  applicant?: { document_kind?: MinorDocumentKind | null } | null;
  guardians?: Guardian[] | null;
  sole_guardian?: SoleGuardian | null;
  birth_certificate?: {
    child_name?: string | null;
    child_birthdate?: string | null;
    parent_names?: string[] | null;
    certificate_number?: string | null;
    issuing_authority?: string | null;
    issue_date?: string | null;
  } | null;
  guardian_match_status?: GuardianMatchStatus | string | null;
  guardian_match_details?: {
    guardian_1?: GuardianMatchEntry | null;
    guardian_2?: GuardianMatchEntry | null;
    child?: GuardianMatchEntry | null;
  } | null;
  guardian_match_override?: GuardianMatchOverride | null;
}

export const isMinorCase = (caseData?: { applicant_type?: string | null } | null): boolean =>
  caseData?.applicant_type === 'minor';

export const DOCUMENT_KIND_LABEL: Record<string, string> = {
  passport: 'Passport',
  id_card: 'ID card',
  birth_certificate: 'Birth certificate',
};

export const documentKindLabel = (kind?: string | null): string =>
  (kind && DOCUMENT_KIND_LABEL[kind]) || 'Not known yet';

export const SOLE_GUARDIAN_REASON_LABEL: Record<string, string> = {
  sole_responsibility: 'Sole parental responsibility',
  deceased: 'The other parent has died',
  court_order: 'Court order',
};

export const GUARDIAN_MATCH_LABEL: Record<string, string> = {
  verified: 'Verified',
  review: 'Needs review',
  unavailable: 'Check unavailable',
};

// The values a lawyer can set. The contract allows one: she confirms what the
// automatic check could not.
export const OVERRIDE_VALUES: { value: string; label: string }[] = [
  { value: 'verified', label: 'Verified' },
];

interface LabelledDocument {
  fileType: string;
  guardianIndex?: number | string | null;
  document_kind?: string | null;
}

// A readable label for the file types only a minor's case carries. Every
// other type keeps showing its raw fileType, as before.
export const minorDocumentLabel = (
  doc: LabelledDocument,
  guardians?: Guardian[] | null
): string | null => {
  switch (doc.fileType) {
    case 'birth_certificate':
      return 'Birth certificate';
    case 'birth_certificate_translation':
      return 'Birth certificate, certified translation (PT)';
    case 'sole_guardian_evidence':
      return 'Proof of sole guardianship';
    case 'guardian_identity': {
      const index = Number(doc.guardianIndex);
      const parent = index === 1 || index === 2 ? `parent ${index}` : 'parent';
      const kind =
        doc.document_kind
        || guardians?.find(g => Number(g.index) === index)?.document_kind
        || 'passport';
      return `${kind === 'id_card' ? 'ID card' : 'Passport'}, ${parent}`;
    }
    default:
      return null;
  }
};

export const fullName = (
  person?: { first_name?: string | null; middle_name?: string | null; last_name?: string | null } | null
): string =>
  [person?.first_name, person?.middle_name, person?.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();

const toDate = (value: any): Date | null => {
  if (!value) return null;
  if (typeof value === 'object' && typeof value._seconds === 'number') {
    return new Date(value._seconds * 1000);
  }
  if (typeof value === 'object' && typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000);
  }
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
};

// Same format as the case list's timestamps.
export const formatTimestamp = (value: any): string => {
  const date = toDate(value);
  if (!date) return '';
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const OTHER_RANK = 7;

const MINOR_DOCUMENT_RANK = (doc: LabelledDocument): number => {
  switch (doc.fileType) {
    case 'proof_of_identity':
      return 0;
    case 'birth_certificate':
      return 1;
    case 'birth_certificate_translation':
      return 2;
    case 'guardian_identity': {
      const index = Number(doc.guardianIndex);
      return index === 1 ? 3 : index === 2 ? 4 : 5;
    }
    case 'sole_guardian_evidence':
      return 6;
    default:
      return OTHER_RANK;
  }
};

// The order the Finanças bundle uses: the child's document, the birth
// certificate and its translation, parent 1, parent 2, the evidence, then everything else in the
// order the API sent it. Front before back within one document.
export const sortMinorDocumentsForDisplay = <T extends LabelledDocument & { docSide?: string }>(
  docs: T[]
): T[] => {
  const sideRank = (d: T) => (d.docSide === 'front' ? 0 : d.docSide === 'back' ? 1 : 2);
  return docs
    .map((doc, position) => ({ doc, position }))
    .sort(
      (a, b) =>
        MINOR_DOCUMENT_RANK(a.doc) - MINOR_DOCUMENT_RANK(b.doc)
        || (MINOR_DOCUMENT_RANK(a.doc) < OTHER_RANK ? sideRank(a.doc) - sideRank(b.doc) : 0)
        || a.position - b.position
    )
    .map(({ doc }) => doc);
};

/** One document the lawyer certifies with the Ordem, and how its panel names it. */
export interface CertifiedDocument {
  person: 'applicant' | 'guardian_1' | 'guardian_2';
  title: string;
  who: string | null;
}

const personName = (entry?: { first_name?: string | null; last_name?: string | null } | null): string | null =>
  [entry?.first_name, entry?.last_name].filter(Boolean).join(' ') || null;

/**
 * The documents a child's case certifies: the child's own passport or ID card
 * (none when the child has only a birth certificate) and each parent's who
 * signs, both unless one signs alone.
 */
export const certifiedDocuments = (
  caseData: MinorCaseFields & { first_name?: string | null; last_name?: string | null }
): CertifiedDocument[] => {
  const documents: CertifiedDocument[] = [];
  if (caseData.applicant?.document_kind !== 'birth_certificate') {
    documents.push({ person: 'applicant', title: 'Ordem certification: child', who: personName(caseData) });
  }
  const indexes = caseData.sole_guardian ? [1] : [1, 2];
  indexes.forEach(index => {
    const guardian = caseData.guardians?.find(g => Number(g.index) === index);
    documents.push({
      person: index === 1 ? 'guardian_1' : 'guardian_2',
      title: `Ordem certification: parent ${index}`,
      who: personName(guardian),
    });
  });
  return documents;
};
